import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';

import LoadingSpinner from '../isLoading';

export default function AllProjectsTable({projects, isLoading, enableFilters = true, enableDensityToggle = true}){
    const [query, setQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [typeFilter, setTypeFilter] = useState("all");
    const [dense, setDense] = useState(false);

    const list = Array.isArray(projects) ? projects : [];

    const filtered = useMemo(() => {
        if(!enableFilters) return list;
        return list.filter(p => {
            const matchesQuery = query.trim().length === 0 || (p.project_name || "").toLowerCase().includes(query.toLowerCase());
            const matchesStatus = statusFilter === "all" ||
                (statusFilter === "pending" && p.status === 0) ||
                (statusFilter === "inprogress" && p.status === 1) ||
                (statusFilter === "feedback" && p.status === 2) ||
                (statusFilter === "cancelled" && p.status === 7) ||
                (statusFilter === "completed" && p.status === 3);
            const matchesType = typeFilter === "all" || (p.task_type || "").toLowerCase() === typeFilter;
            return matchesQuery && matchesStatus && matchesType;
        });
    }, [list, query, statusFilter, typeFilter, enableFilters]);

    const cellPadding = dense ? "px-4 py-2" : "px-6 py-4";

    return(
        <>
            {enableFilters && (
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-2">
                        <input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search projects..."
                            className="h-9 w-64 rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-gray-600"
                        />
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="h-9 rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-gray-600"
                        >
                            <option value="all">All status</option>
                            <option value="pending">Pending</option>
                            <option value="inprogress">In progress</option>
                            <option value="feedback">Design ready</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                        </select>
                        <select
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value)}
                            className="h-9 rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-gray-600"
                        >
                            <option value="all">All types</option>
                            <option value="graphic">Graphic</option>
                            <option value="video">Video</option>
                        </select>
                    </div>
                    {enableDensityToggle && (
                        <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 select-none">
                            <input type="checkbox" checked={dense} onChange={(e) => setDense(e.target.checked)} className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700" />
                            Compact rows
                        </label>
                    )}
                </div>
            )}

            <div className='overflow-x-auto'>
                <div className="max-h-[60vh] overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-700">
                    <table className="min-w-full table-fixed divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10">
                        <tr>
                            <th className={`w-2/5 ${cellPadding} text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider`}>Project Name</th>
                            <th className={`w-1/12 ${cellPadding} text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider`}>Type</th>
                            <th className={`w-1/6 ${cellPadding} text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider`}>Estimated Delivery</th>
                            <th className={`w-1/6 ${cellPadding} text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider`}>Brand</th>
                            <th className={`w-1/6 ${cellPadding} text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider`}>Status</th>
                            <th className={`w-1/6 ${cellPadding} text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider`}>Created Date</th>
                        </tr>
                        </thead>

                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {isLoading && <tr><td colSpan={6}><div className='py-10'><LoadingSpinner /></div></td></tr>}
                        
                        {!isLoading && filtered.length > 0 ? (
                            filtered.map(project => {

                                const project_status = () => {
                                    
                                    if(project.status === 0){
                                        return <span className='inline-flex items-center rounded-full border border-red-200 bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700'>Pending</span>
                                    } else if(project.status === 1){
                                        return <span className='inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700'>In Progress</span>
                                    } else if(project.status === 2){
                                        return <span className='inline-flex items-center rounded-full border border-purple-200 bg-purple px-2.5 py-0.5 text-xs font-medium text-white'>Design ready for feedback</span>
                                    } else if(project.status === 3){
                                        return <span className='inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700'>Completed</span>
                                    } else if(project.status === 7){
                                        return <span className='inline-flex items-center rounded-full border border-gray-300 bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700'>Cancelled</span>
                                    } else {
                                        return <span className='inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs font-medium text-gray-600'>Unknown</span>
                                    }
                                }

                                const project_type = () => {
                                    if(project.task_type === "graphic"){
                                        return <span className='inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800'>Graphic</span>
                                    } else {
                                        return <span className='inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800'>Video</span>
                                    }
                                }

                                const estdelivery = () => {
                                    if (project.started_at === null) {
                                        return 'N/A';
                                    }

                                    const projectDate = moment(project.started_at);
                                    const dayOfWeek = projectDate.day(); // 0 = Sunday, 6 = Saturday
                                    let estimated;

                                    if (project.task_type === 'graphic') {
                                        if (dayOfWeek === 5) { // Friday
                                            estimated = projectDate.clone().add(72, 'hours');
                                        } else if (dayOfWeek === 6) { // Saturday
                                            estimated = projectDate.clone().add(48, 'hours');
                                        } else {
                                            estimated = projectDate.clone().add(24, 'hours');
                                        }
                                    } else { // video
                                        if (dayOfWeek === 5) { // Friday
                                            estimated = projectDate.clone().add(6, 'days');
                                        } else if (dayOfWeek === 6) { // Saturday
                                            estimated = projectDate.clone().add(5, 'days');
                                        } else {
                                            estimated = projectDate.clone().add(4, 'days');
                                        }
                                    }

                                    return estimated.format("DD-MM-YYYY HH:mm:ss");
                                }

                                const createdAt = () => {
                                    const parsedDateTime = new Date(project.createdAt);
                                    return parsedDateTime.toLocaleString();
                                }

                                const newMessagesCount = project.newMessages || project.unreadMessages || 0;

                                return(
                                    <tr className='odd:bg-white dark:odd:bg-gray-800 even:bg-gray-50 dark:even:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150' key={project.id}>
                                        <td className={`${cellPadding} whitespace-nowrap`}>
                                            <div className="flex items-center min-w-0 gap-2">
                                                <Link to={`/manage/project/${project.id}`} className='text-sm font-semibold text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-150 truncate'>
                                                    {project.project_name}
                                                </Link>
                                                {newMessagesCount > 0 && (
                                                    <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 rounded-full bg-red-500 text-white text-xs font-semibold">
                                                        {newMessagesCount > 99 ? '99+' : newMessagesCount}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className={`${cellPadding} whitespace-nowrap`}>{project_type()}</td>
                                        <td className={`${cellPadding} whitespace-nowrap text-sm text-gray-700 dark:text-gray-300`}>{estdelivery()}</td>
                                        <td className={`${cellPadding} whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 truncate`}>{project.brand.brand_name}</td>
                                        <td className={`${cellPadding} whitespace-nowrap`}>{project_status()}</td>
                                        <td className={`${cellPadding} whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 text-right`}>{createdAt()}</td>
                                    </tr>
                                )
                            })
                        ) : (
                            <tr>
                                <td colSpan={6}>
                                    <div className='text-center py-12'>
                                        <div className="text-gray-400 dark:text-gray-500 text-lg font-medium">No Projects found</div>
                                        <div className="text-gray-400 dark:text-gray-500 text-sm mt-1">Projects will appear here once they are created</div>
                                    </div>
                                </td>
                            </tr>
                        )}
                        
                        </tbody>
                    </table>
                </div>
            </div>
        
        </>
    )
}